/**
 * Módulo: Calculadora de macros (cliente)
 * =============================================================================
 * Carpeta: modules/macro-calculator/
 * Bundle: dist/js/main.js (import en src/js/modules/index.js)
 * Markup: modules/macro-calculator/template.php
 * Estilos: modules/macro-calculator/less/_macro-calculator.less
 *
 * FLUJO (mismo orden en todos los archivos del módulo):
 *
 *   DOMContentLoaded
 *        └── initMacroCalculator( [data-macro-calculator] )
 *                  ├── enlaza #macro-calculator-form (input/change)
 *                  └── run() en cada cambio
 *                            ├── readForm()        → objeto inputs
 *                            ├── calculate()       → BMR → TDEE → objetivo → macros
 *                            └── renderResults()   → DOM + CTA + evento
 *
 * Evento para integraciones (carruseles, dietas, etc.):
 *   document.addEventListener('titanfit:macro-calculated', (e) => {
 *     const { inputs, results } = e.detail;
 *   });
 */

import { initFormFieldValidation } from '../../../src/js/general/form-field-validation.js';

/* =============================================================================
   1. CONSTANTES — Coeficientes del modelo nutricional
   ============================================================================= */

/** PAL: actividad diaria SIN contar sesiones de gym (factor × BMR) */
const PAL = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  high: 1.725,
  very_high: 1.9,
};

/** MET: gasto del entreno (kcal ≈ MET × kg × horas por sesión) */
const MET = {
  strength: 5.0,
  mixed: 6.0,
  cardio_moderate: 7.0,
  cardio_intense: 9.0,
};

/** Ajuste kcal/día sobre el TDEE según meta elegida */
const GOAL_ADJUST = {
  lose_aggressive: -1100,
  lose_moderate: -550,
  lose_soft: -275,
  maintain: 0,
  gain_moderate: 250,
  gain_aggressive: 400,
  recomp: -150,
};

/** Proteína (g/kg) priorizada en déficit/volumen/recomp */
const PROTEIN_PER_KG = {
  lose_aggressive: 2.2,
  lose_moderate: 2.0,
  lose_soft: 1.9,
  maintain: 1.7,
  gain_moderate: 1.9,
  gain_aggressive: 1.7,
  recomp: 2.1,
};

/** Límites de seguridad y conversión orientativa kg/semana */
const MIN_KCAL = { male: 1500, female: 1200 };
const MAX_DEFICIT_RATIO = 0.25;
const KCAL_PER_KG_FAT = 7700;

/** Conversión imperial → métrico (internamente siempre kg/cm) */
const IMPERIAL = {
  lbToKg: 0.45359237,
  inToCm: 2.54,
};

/** Selectores enlazados con la plantilla PHP (prefijo mc-) */
const SELECTORS = {
  form: '#macro-calculator-form',
  height: '#mc-height',
  weight: '#mc-weight',
  units: '#mc-units',
  kcal: '#mc-result-kcal',
  tdee: '#mc-result-tdee',
  formula: '#mc-result-formula',
  protein: '#mc-result-protein',
  carbs: '#mc-result-carbs',
  fat: '#mc-result-fat',
  notices: '#mc-result-notices',
  cta: '#macro-calculator-cta',
  labelHeight: '[data-mc-label-height]',
  labelWeight: '[data-mc-label-weight]',
};

/** Límites del motor de cálculo (siempre kg/cm tras convertir) */
const METRIC_BOUNDS = {
  heightCm: { min: 120, max: 230 },
  weightKg: { min: 30, max: 250 },
};

/** Límites del input según unidad mostrada (imperial derivado de METRIC_BOUNDS) */
const UNIT_LIMITS = {
  metric: {
    height: { min: METRIC_BOUNDS.heightCm.min, max: METRIC_BOUNDS.heightCm.max, step: 1 },
    weight: { min: 35, max: 200, step: 0.1 },
    heightLabel: 'Altura (cm)',
    weightLabel: 'Peso (kg)',
  },
  imperial: {
    height: {
      min: Math.ceil(METRIC_BOUNDS.heightCm.min / IMPERIAL.inToCm),
      max: Math.floor(METRIC_BOUNDS.heightCm.max / IMPERIAL.inToCm),
      step: 0.5,
    },
    weight: { min: 80, max: 440, step: 0.5 },
    heightLabel: 'Altura (pulgadas)',
    weightLabel: 'Peso (lb)',
  },
};

/* =============================================================================
   2. UTILIDADES
   ============================================================================= */

/** Redondeo a 5 g (macros legibles en cocina/báscula) */
function round5(n) {
  return Math.round(n / 5) * 5;
}

/** % grasa válido para fórmulas basadas en masa magra */
function hasBodyfat(bodyfatPct) {
  const bf = parseFloat(bodyfatPct);
  return !Number.isNaN(bf) && bf > 0 && bf < 70;
}

/* =============================================================================
   3. UNIDADES — Siempre calculamos en kg/cm; la UI puede ser imperial
   ============================================================================= */

function toMetric(inputs) {
  let height = parseFloat(inputs.height);
  let weight = parseFloat(inputs.weight);
  if (inputs.units === 'imperial') {
    height *= IMPERIAL.inToCm;
    weight *= IMPERIAL.lbToKg;
  }
  return { ...inputs, heightCm: height, weightKg: weight };
}

/** Guarda altura/peso en data-metric-value (fuente de verdad en cm/kg) */
function syncMetricDataset(heightInput, weightInput, displayUnits) {
  let h = parseFloat(heightInput.value);
  let w = parseFloat(weightInput.value);
  if (displayUnits === 'imperial') {
    h *= IMPERIAL.inToCm;
    w *= IMPERIAL.lbToKg;
  }
  heightInput.dataset.metricValue = String(Math.round(h * 10) / 10);
  weightInput.dataset.metricValue = String(Math.round(w * 10) / 10);
}

/** Pinta inputs según data-metric-value y el sistema elegido */
function applyUnitDisplay(heightInput, weightInput, displayUnits) {
  const hCm = parseFloat(heightInput.dataset.metricValue);
  const wKg = parseFloat(weightInput.dataset.metricValue);
  if (displayUnits === 'imperial') {
    heightInput.value = String(Math.round((hCm / IMPERIAL.inToCm) * 10) / 10);
    weightInput.value = String(Math.round((wKg / IMPERIAL.lbToKg) * 10) / 10);
  } else {
    heightInput.value = String(Math.round(hCm));
    weightInput.value = String(Math.round(wKg * 10) / 10);
  }
}

function updateUnitLabels(root, displayUnits) {
  const cfg = UNIT_LIMITS[displayUnits] || UNIT_LIMITS.metric;
  const heightLabel = root.querySelector(SELECTORS.labelHeight);
  const weightLabel = root.querySelector(SELECTORS.labelWeight);
  const heightInput = root.querySelector(SELECTORS.height);
  const weightInput = root.querySelector(SELECTORS.weight);

  if (heightLabel) heightLabel.textContent = cfg.heightLabel;
  if (weightLabel) weightLabel.textContent = cfg.weightLabel;
  if (heightInput) {
    heightInput.min = cfg.height.min;
    heightInput.max = cfg.height.max;
    heightInput.step = cfg.height.step;
  }
  if (weightInput) {
    weightInput.min = cfg.weight.min;
    weightInput.max = cfg.weight.max;
    weightInput.step = cfg.weight.step;
  }

  syncValidateBounds(heightInput, weightInput, displayUnits);
}

/** Alinea data-validate-min/max con los límites mostrados (cm/kg o pulgadas/lb) */
function syncValidateBounds(heightInput, weightInput, displayUnits) {
  const cfg = UNIT_LIMITS[displayUnits] || UNIT_LIMITS.metric;
  if (heightInput) {
    heightInput.setAttribute('data-validate-min', String(cfg.height.min));
    heightInput.setAttribute('data-validate-max', String(cfg.height.max));
  }
  if (weightInput) {
    weightInput.setAttribute('data-validate-min', String(cfg.weight.min));
    weightInput.setAttribute('data-validate-max', String(cfg.weight.max));
  }
}

/* =============================================================================
   4. METABOLISMO BASAL (BMR) — Fórmulas; gym: Katch si hay % grasa
   ============================================================================= */

function calcBmrMifflin(sex, weightKg, heightCm, age) {
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age;
  return sex === 'male' ? base + 5 : base - 161;
}

function calcBmrHarris(sex, weightKg, heightCm, age) {
  if (sex === 'male') {
    return 88.362 + 13.397 * weightKg + 4.799 * heightCm - 5.677 * age;
  }
  return 447.593 + 9.247 * weightKg + 3.098 * heightCm - 4.33 * age;
}

function calcLeanMass(weightKg, bodyfatPct) {
  return weightKg * (1 - bodyfatPct / 100);
}

function calcBmrKatch(leanMass) {
  return 370 + 21.6 * leanMass;
}

function calcBmrCunningham(leanMass) {
  return 500 + 22 * leanMass;
}

/**
 * Resuelve BMR según fórmula elegida y % grasa opcional.
 * Regla gym: Mifflin por defecto → si hay % grasa válido, usamos Katch–McArdle.
 */
function calcBmr(inputs, metric) {
  const { sex, age, weightKg, heightCm } = metric;
  const bf = parseFloat(inputs.bodyfatPct);
  const bfOk = hasBodyfat(inputs.bodyfatPct);
  let formula = inputs.formula;

  if (bfOk && formula === 'mifflin') {
    formula = 'katch';
  }

  if ((formula === 'katch' || formula === 'cunningham') && !bfOk) {
    return {
      bmr: calcBmrMifflin(sex, weightKg, heightCm, age),
      formulaUsed: 'Mifflin–St Jeor',
      fallback: true,
    };
  }

  if (formula === 'katch') {
    return {
      bmr: calcBmrKatch(calcLeanMass(weightKg, bf)),
      formulaUsed: 'Katch–McArdle',
      fallback: false,
    };
  }

  if (formula === 'cunningham') {
    return {
      bmr: calcBmrCunningham(calcLeanMass(weightKg, bf)),
      formulaUsed: 'Cunningham',
      fallback: false,
    };
  }

  if (formula === 'harris') {
    return {
      bmr: calcBmrHarris(sex, weightKg, heightCm, age),
      formulaUsed: 'Harris–Benedict (revisada)',
      fallback: false,
    };
  }

  return {
    bmr: calcBmrMifflin(sex, weightKg, heightCm, age),
    formulaUsed: 'Mifflin–St Jeor',
    fallback: false,
  };
}

/* =============================================================================
   5. TDEE — Vida diaria (PAL) + entreno (MET), separados para no duplicar
   ============================================================================= */

function calcTrainingKcalPerDay(weightKg, days, minutes, trainingType) {
  const d = parseInt(days, 10) || 0;
  const m = parseInt(minutes, 10) || 0;
  if (d === 0 || m === 0) return 0;
  const met = MET[trainingType] || MET.strength;
  const perSession = met * weightKg * (m / 60);
  return (perSession * d) / 7;
}

function calcTdee(bmr, dailyActivityKey, trainingKcalDay) {
  return bmr * (PAL[dailyActivityKey] || PAL.sedentary) + trainingKcalDay;
}

/* =============================================================================
   6. OBJETIVO CALÓRICO Y MACROS — Déficit/superávit + reparto P → G → C
   ============================================================================= */

/**
 * @param {boolean} safeCapEnabled Checkbox «tope seguro» (por defecto activo).
 */
function calcTargetKcal(tdee, goal, sex, safeCapEnabled) {
  const adj = GOAL_ADJUST[goal] ?? 0;
  const floor = MIN_KCAL[sex] || 1200;
  const maxDeficit = tdee * MAX_DEFICIT_RATIO;
  const requestedDeficit = adj < 0 ? Math.abs(adj) : 0;
  const exceedsSafeDeficit = adj < 0 && requestedDeficit > maxDeficit;

  let target = tdee + adj;

  if (exceedsSafeDeficit && safeCapEnabled) {
    target = tdee - maxDeficit;
  }

  const targetKcal = Math.max(floor, Math.round(target));
  const actualDeficit = Math.max(0, tdee - targetKcal);

  return {
    targetKcal,
    exceedsSafeDeficit,
    deficitCapped: exceedsSafeDeficit && safeCapEnabled,
    safeCapEnabled: Boolean(safeCapEnabled),
    actualDeficit,
  };
}

/** Proteína y grasa mínima primero; carbohidratos = kcal restantes */
function calcMacros(targetKcal, weightKg, goal) {
  const proteinG = round5(weightKg * (PROTEIN_PER_KG[goal] ?? 1.8));
  const proteinKcal = proteinG * 4;

  let fatG = round5(Math.max(weightKg * 0.8, (targetKcal * 0.25) / 9));
  let carbKcal = targetKcal - proteinKcal - fatG * 9;

  if (carbKcal < 0) {
    fatG = round5(Math.max(weightKg * 0.6, (targetKcal - proteinKcal) / 9));
    carbKcal = targetKcal - proteinKcal - fatG * 9;
  }

  return {
    protein: proteinG,
    carbs: round5(Math.max(0, carbKcal / 4)),
    fat: fatG,
  };
}

/* =============================================================================
   7. AVISOS — Contexto para el usuario (seguridad, gym, fórmulas)
   ============================================================================= */

function buildNotices(inputs, context, tdee, goal, bmrInfo, targetInfo) {
  const notices = [];
  const weeklyEst = (deficit) =>
    Math.round(((deficit * 7) / KCAL_PER_KG_FAT) * 100) / 100;

  if (bmrInfo.fallback) {
    notices.push('Indica tu % de grasa o elige Mifflin–St Jeor para un cálculo más preciso en gym.');
  }

  if (goal.startsWith('lose_') && targetInfo.deficitCapped) {
    notices.push(
      `Tope seguro activo: déficit ajustado al 25 % del mantenimiento (~${weeklyEst(targetInfo.actualDeficit)} kg/semana estimados).`
    );
  }

  if (goal.startsWith('lose_') && targetInfo.exceedsSafeDeficit && !targetInfo.safeCapEnabled) {
    notices.push(
      `Sin tope seguro: la meta supera el 25 % de déficit recomendado (~${weeklyEst(targetInfo.actualDeficit)} kg/semana). Valora supervisión profesional.`
    );
  }

  if (goal === 'lose_aggressive') {
    notices.push('Pérdida agresiva: ideal con supervisión. Prioriza proteína y sueño.');
  }

  if (goal === 'gain_aggressive') {
    notices.push('Volumen agresivo: monitoriza grasa corporal cada 2–4 semanas.');
  }

  if (goal === 'recomp') {
    notices.push('Recomposición: déficit ligero y proteína alta; progresión de fuerza constante.');
  }

  if (parseInt(inputs.training_days, 10) >= 6) {
    notices.push('Con 6–7 sesiones semanales, vigila fatiga y recuperación.');
  }

  if (hasBodyfat(inputs.bodyfatPct) && inputs.formula === 'mifflin') {
    notices.push('Con % de grasa indicado aplicamos Katch–McArdle (estándar en fitness).');
  }

  if (context.age < 18) {
    notices.push('Menores de 18: consulta a un profesional antes de aplicar déficits.');
  }

  return notices;
}

/* =============================================================================
   8. ORQUESTADOR — calculate(): une todos los pasos del modelo
   ============================================================================= */

function calculate(inputs) {
  const metric = toMetric(inputs);
  const age = parseInt(inputs.age, 10);

  if (Number.isNaN(age) || age < 15 || age > 80) {
    return { valid: false };
  }

  const { weightKg, heightCm } = metric;
  const { heightCm: h, weightKg: w } = METRIC_BOUNDS;
  if (
    weightKg < w.min ||
    weightKg > w.max ||
    heightCm < h.min ||
    heightCm > h.max
  ) {
    return { valid: false };
  }

  const bmrInfo = calcBmr(inputs, { ...metric, age });
  const trainingKcalDay = calcTrainingKcalPerDay(
    weightKg,
    inputs.training_days,
    inputs.training_minutes,
    inputs.training_type
  );
  const tdee = Math.round(calcTdee(bmrInfo.bmr, inputs.daily_activity, trainingKcalDay));
  const safeCap = Boolean(inputs.safe_cap);
  const targetInfo = calcTargetKcal(tdee, inputs.goal, inputs.sex, safeCap);
  const macros = calcMacros(targetInfo.targetKcal, weightKg, inputs.goal);
  const notices = buildNotices(inputs, { ...metric, age }, tdee, inputs.goal, bmrInfo, targetInfo);

  const weeklyChangeKg = inputs.goal.startsWith('lose_')
    ? ((tdee - targetInfo.targetKcal) * 7) / KCAL_PER_KG_FAT
    : inputs.goal.startsWith('gain_')
      ? ((targetInfo.targetKcal - tdee) * 7) / KCAL_PER_KG_FAT
      : 0;

  return {
    valid: true,
    bmr: Math.round(bmrInfo.bmr),
    formulaUsed: bmrInfo.formulaUsed,
    tdee,
    targetKcal: targetInfo.targetKcal,
    targetInfo,
    macros,
    notices,
    weeklyChangeKg: Math.round(weeklyChangeKg * 100) / 100,
    trainingKcalDay: Math.round(trainingKcalDay),
  };
}

/* =============================================================================
   9. UI — Formulario: lectura de campos (names = plantilla PHP)
   ============================================================================= */

function readForm(form) {
  const fd = new FormData(form);
  return {
    sex: fd.get('sex'),
    age: fd.get('age'),
    units: fd.get('units'),
    height: fd.get('height'),
    weight: fd.get('weight'),
    daily_activity: fd.get('daily_activity'),
    training_days: fd.get('training_days'),
    training_minutes: fd.get('training_minutes'),
    training_type: fd.get('training_type'),
    goal: fd.get('goal'),
    formula: fd.get('formula'),
    bodyfatPct: fd.get('bodyfat'),
    safe_cap: form.querySelector('#mc-safe-cap')?.checked ?? true,
  };
}

/** Muestra el checkbox de tope seguro solo en metas de pérdida de grasa */
function updateSafeCapVisibility(form) {
  const wrap = form.querySelector('#mc-safe-cap-wrap');
  const goal = form.querySelector('#mc-goal')?.value || '';
  if (wrap) {
    wrap.hidden = !goal.startsWith('lose_');
  }
}

/* =============================================================================
   10. UI — Resultados, CTA y evento global
   ============================================================================= */

function clearResults(root) {
  const set = (sel, text) => {
    const el = root.querySelector(sel);
    if (el) el.textContent = text;
  };

  set(SELECTORS.kcal, '—');
  set(SELECTORS.tdee, '—');
  set(SELECTORS.formula, '');
  set(SELECTORS.protein, '—');
  set(SELECTORS.carbs, '—');
  set(SELECTORS.fat, '—');

  const noticesEl = root.querySelector(SELECTORS.notices);
  if (noticesEl) {
    noticesEl.hidden = true;
    noticesEl.innerHTML = '';
  }

  const ctaEl = root.querySelector(SELECTORS.cta);
  if (ctaEl) {
    ctaEl.hidden = true;
    ctaEl.setAttribute('aria-hidden', 'true');
  }
}

function renderResults(root, data, inputs) {
  if (!data.valid) {
    clearResults(root);
    return;
  }

  const kcalEl = root.querySelector(SELECTORS.kcal);
  const tdeeEl = root.querySelector(SELECTORS.tdee);
  const formulaEl = root.querySelector(SELECTORS.formula);
  const proteinEl = root.querySelector(SELECTORS.protein);
  const carbsEl = root.querySelector(SELECTORS.carbs);
  const fatEl = root.querySelector(SELECTORS.fat);
  const noticesEl = root.querySelector(SELECTORS.notices);
  const ctaEl = root.querySelector(SELECTORS.cta);

  if (kcalEl) kcalEl.textContent = String(data.targetKcal);
  if (tdeeEl) tdeeEl.textContent = String(data.tdee);
  if (formulaEl) {
    formulaEl.textContent = `Metabolismo basal: ${data.formulaUsed} · BMR ${data.bmr} kcal`;
  }
  if (proteinEl) {
    proteinEl.textContent = `${data.macros.protein} g`;
    proteinEl.setAttribute('aria-label', `${data.macros.protein} gramos de proteína`);
  }
  if (carbsEl) {
    carbsEl.textContent = `${data.macros.carbs} g`;
    carbsEl.setAttribute('aria-label', `${data.macros.carbs} gramos de carbohidratos`);
  }
  if (fatEl) {
    fatEl.textContent = `${data.macros.fat} g`;
    fatEl.setAttribute('aria-label', `${data.macros.fat} gramos de grasa`);
  }

  if (noticesEl) {
    noticesEl.replaceChildren();
    data.notices.forEach((text) => {
      const li = document.createElement('li');
      li.textContent = text;
      noticesEl.appendChild(li);
    });
    noticesEl.hidden = data.notices.length === 0;
  }

  if (ctaEl) {
    ctaEl.hidden = false;
    ctaEl.setAttribute('aria-hidden', 'false');
  }

  document.dispatchEvent(
    new CustomEvent('titanfit:macro-calculated', {
      bubbles: true,
      detail: { inputs, results: data },
    })
  );
}

/* =============================================================================
   11. UI — Inicialización de una instancia en la página
   ============================================================================= */

function initMacroCalculator(root) {
  if (!root) return;

  const form = root.querySelector(SELECTORS.form);
  if (!form) return;

  const heightInput = form.querySelector(SELECTORS.height);
  const weightInput = form.querySelector(SELECTORS.weight);
  const unitsSelect = form.querySelector(SELECTORS.units);

  if (heightInput) heightInput.dataset.metricValue = heightInput.value;
  if (weightInput) weightInput.dataset.metricValue = weightInput.value;

  const fieldValidation = initFormFieldValidation(form);
  updateUnitLabels(root, unitsSelect?.value || 'metric');

  /** Ciclo principal: validación → cálculo → pantalla */
  const run = () => {
    updateSafeCapVisibility(form);
    const formOk = fieldValidation.validateAll();
    const inputs = readForm(form);
    const data = formOk ? calculate(inputs) : { valid: false };
    renderResults(root, data, inputs);
  };

  form.addEventListener('input', run);
  form.addEventListener('change', run);
  form.querySelector('#mc-goal')?.addEventListener('change', run);

  unitsSelect?.addEventListener('change', (ev) => {
    const nextUnits = ev.target.value;
    const prevUnits = nextUnits === 'imperial' ? 'metric' : 'imperial';
    syncMetricDataset(heightInput, weightInput, prevUnits);
    applyUnitDisplay(heightInput, weightInput, nextUnits);
    updateUnitLabels(root, nextUnits);
    run();
  });

  const onBodyMetricInput = () => {
    syncMetricDataset(heightInput, weightInput, unitsSelect?.value || 'metric');
  };
  heightInput?.addEventListener('input', onBodyMetricInput);
  weightInput?.addEventListener('input', onBodyMetricInput);

  run();
}

/* =============================================================================
   12. ARRANQUE
   ============================================================================= */

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-macro-calculator]').forEach((el) => initMacroCalculator(el));
});
